import React, { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import Hls from "hls.js";

export interface HlsPlayerHandle {
    /** 현재 재생 위치를 초 단위(floor)로 반환 */
    getCurrentTime: () => number;
}

interface HlsPlayerProps {
    src: string;
    /** 이어보기 시작 위치(초). loadedmetadata 시점에 1회 적용 */
    startTime?: number;
    autoPlay?: boolean;
    muted?: boolean;
    controls?: boolean;
    className?: string;
    onPlay?: () => void;
    onPause?: () => void;
    /** 영상 메타데이터 로드 완료(재생 준비 완료) */
    onReady?: () => void;
}

/**
 * HLS(.m3u8) 재생용 공통 컴포넌트.
 * - iOS WebView: `<video>`의 네이티브 HLS 지원을 사용
 * - Android WebView 등: hls.js로 MSE 기반 재생
 *
 * 화질은 ABR(Adaptive Bitrate)로 자동 조절된다.
 * 시청시간 추적이 필요한 화면에서는 ref.getCurrentTime() 으로 현재 위치를 조회한다.
 */
const HlsPlayer = forwardRef<HlsPlayerHandle, HlsPlayerProps>(
    ({ src, startTime = 0, autoPlay = false, muted = false, controls = true, className, onPlay, onPause, onReady }, ref) => {
        const videoRef = useRef<HTMLVideoElement | null>(null);
        const startTimeRef = useRef<number>(startTime);

        // startTime은 prop으로 받지만, loadedmetadata 콜백에서 stale closure를 피하기 위해 ref로 미러링한다.
        useEffect(() => {
            startTimeRef.current = startTime;
        }, [startTime]);

        useImperativeHandle(
            ref,
            () => ({
                getCurrentTime: () => Math.floor(videoRef.current?.currentTime ?? 0),
            }),
            [],
        );

        // 소스 부착(네이티브 HLS / hls.js 분기)
        useEffect(() => {
            const video = videoRef.current;
            if (!video || !src) return;

            let hls: Hls | null = null;

            if (video.canPlayType("application/vnd.apple.mpegurl")) {
                // iOS Safari / WKWebView 네이티브 HLS
                video.src = src;
            } else if (Hls.isSupported()) {
                hls = new Hls({ enableWorker: true });
                hls.loadSource(src);
                hls.attachMedia(video);
            } else {
                // 최후 폴백 — 일부 환경에서는 그래도 src로 시도
                video.src = src;
            }

            return () => {
                hls?.destroy();
            };
        }, [src]);

        // 이벤트 바인딩
        useEffect(() => {
            const video = videoRef.current;
            if (!video) return;

            const handleLoadedMeta = () => {
                if (startTimeRef.current > 0) {
                    try {
                        video.currentTime = startTimeRef.current;
                    } catch {
                        // 일부 환경에서 seek 실패 시 무시 — 0초부터 재생
                    }
                }
                onReady?.();
            };
            const handlePlay = () => onPlay?.();
            const handlePause = () => onPause?.();

            video.addEventListener("loadedmetadata", handleLoadedMeta);
            video.addEventListener("play", handlePlay);
            video.addEventListener("pause", handlePause);

            return () => {
                video.removeEventListener("loadedmetadata", handleLoadedMeta);
                video.removeEventListener("play", handlePlay);
                video.removeEventListener("pause", handlePause);
            };
        }, [src, onPlay, onPause, onReady]);

        return (
            <video
                ref={videoRef}
                className={className}
                autoPlay={autoPlay}
                muted={muted}
                playsInline
                controls={controls}
            />
        );
    },
);

HlsPlayer.displayName = "HlsPlayer";

export default HlsPlayer;
